<template>
    <div class="h-100 d-flex flex-column">
        <div v-if="loading" class="text-center p-5">
            <b-spinner variant="primary" label="Loading..."></b-spinner>
        </div>
        <div v-else-if="detailData" class="flex-grow-1" :class="{'overflow-hidden': !isMobile}">
            <div class="row m-0" :class="{'h-100': !isMobile}">
                <!-- Left Column: Chart & Info -->
                <div class="col-12 col-md-7 p-0 d-flex flex-column order-2 order-md-1" :class="{'h-100 border-right overflow-auto': !isMobile}">
                    <!-- 1. K-Line Chart Area -->
                    <div class="kline-container bg-white border-bottom position-relative" style="min-height: 400px; flex: 1;">
                        <!-- Chart Header (Stock Info) -->
                        <div class="position-absolute p-2 d-flex justify-content-between w-100" style="z-index: 10; background: rgba(255,255,255,0.8);">
                            <div @click="openStockSidebar" style="cursor: pointer;">
                                <span class="font-weight-bold mr-2">{{ stock ? stock.stock_name : '' }}</span>
                                <span class="small text-secondary">{{ stock ? stock.stock_code : '' }}</span>
                            </div>
                            <div>
                                <span :class="getColorClass(detailData.detail.quote_change)" class="font-weight-bold">{{ detailData.detail.cur_price }}</span>
                                <span :class="getColorClass(detailData.detail.quote_change)" class="small ml-1">{{ detailData.detail.quote_change }}%</span>
                            </div>
                        </div>
                        
                        <stock-kline-day v-if="stock" :symbol_code="stock.stock_code" chart_height="100%" :title="''" :subText="''" />
                    </div>

                    <!-- 2. Statistics Bar -->
                    <div class="d-flex bg-white border-bottom text-center small py-2 flex-shrink-0">
                        <div class="flex-fill border-right">
                            <div class="text-secondary mb-1">换手率</div>
                            <div>{{ detailData.detail.turnover_ratio }}%</div>
                        </div>
                        <div class="flex-fill border-right">
                            <div class="text-secondary mb-1">成交额</div>
                            <div>{{ formatMoney(detailData.detail.turnover) }}</div>
                        </div>
                        <div class="flex-fill border-right">
                            <div class="text-secondary mb-1">净买入</div>
                            <div :class="getColorClass(detailData.detail.buy_in)">{{ formatMoney(detailData.detail.buy_in) }}</div>
                        </div>
                        <div class="flex-fill">
                            <div class="text-secondary mb-1">上榜数</div>
                            <div>{{ detailData.detail.join_num }}</div>
                        </div>
                    </div>
                    
                    <!-- 3. Reason Section -->
                    <div class="p-2 bg-light border-bottom small text-secondary flex-shrink-0">
                        <span class="font-weight-bold text-dark mr-1">上榜理由:</span> {{ detailData.detail.up_reason }}
                    </div>
                </div>

                <!-- Right Column: Seat Lists -->
                <div class="col-12 col-md-5 p-0 bg-light order-1 order-md-2" :class="{'h-100 overflow-auto': !isMobile}">
                    <div v-for="(group, gIdx) in traderGroups" :key="'g'+gIdx">
                        <!-- Group Header (Reason) -->
                        <div class="p-2 bg-light border-bottom small font-weight-bold text-dark border-top" v-if="traderGroups.length > 1">
                            {{ group.reason_label }}
                        </div>
                        
                        <div class="row m-0">
                            <!-- Buy List -->
                            <div class="col-12 col-md-12 p-0 border-bottom">
                                <div class="p-2 border-bottom small font-weight-bold text-secondary d-flex justify-content-between" style="background-color: #f8f9fa;">
                                    <span>买入前五营业部</span>
                                    <span class="text-danger">总计: {{ formatMoney(group.buy_total) }} ({{ calcPct(group.buy_total) }})</span>
                                </div>
                                <div v-for="(trader, idx) in group.buy" :key="'buy'+gIdx+idx" class="d-flex p-2 border-bottom align-items-center bg-white">
                                    <!-- Rank Badge -->
                                    <div class="mr-2">
                                        <span class="badge badge-danger text-white d-flex align-items-center justify-content-center" style="width: 18px; height: 18px; padding: 0;">{{ idx + 1 }}</span>
                                    </div>
                                    <!-- Name & Tags -->
                                    <div style="flex: 1; min-width: 0;">
                                        <div class="d-flex align-items-center mb-1">
                                            <span class="font-weight-bold text-dark small text-truncate">
                                                {{ trader.trader_name }}
                                            </span>
                                            <b-badge v-if="getYouziLabel(trader.youzi_icon)" variant="secondary" class="ml-1 scale-8 font-weight-normal text-white bg-secondary border-0 flex-shrink-0">
                                                {{ getYouziLabel(trader.youzi_icon) }}
                                            </b-badge>
                                            <template v-if="trader.group_icon">
                                                <span v-for="(icon, idx) in parseGroupIcon(trader.group_icon)" :key="'gi-buy'+idx" 
                                                    class="badge badge-danger text-white ml-1 scale-8 font-weight-normal flex-shrink-0">
                                                    {{ icon }}
                                                </span>
                                            </template>
                                        </div>
                                    </div>
                                    <!-- Amount -->
                                    <div class="text-right ml-2" style="min-width: 130px;">
                                        <div class="text-danger font-weight-bold">
                                            {{ formatMoney(trader.buy_amount) }}
                                            <span class="small ml-1">({{ calcPct(trader.buy_amount) }})</span>
                                        </div>
                                        <div v-if="trader.sell_amount > 0" class="text-success scale-8 small">
                                            卖:{{ formatMoney(trader.sell_amount) }}
                                            <span class="ml-1">({{ calcPct(trader.sell_amount) }})</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
            
                            <!-- Sell List -->
                            <div class="col-12 col-md-12 p-0">
                                <div class="p-2 border-bottom small font-weight-bold text-secondary d-flex justify-content-between" style="background-color: #f8f9fa;">
                                    <span>卖出前五营业部</span>
                                    <span class="text-success">总计: {{ formatMoney(group.sell_total) }} ({{ calcPct(group.sell_total) }})</span>
                                </div>
                                <div v-for="(trader, idx) in group.sell" :key="'sell'+gIdx+idx" class="d-flex p-2 border-bottom align-items-center bg-white">
                                    <!-- Rank Badge -->
                                    <div class="mr-2">
                                        <span class="badge badge-success text-white d-flex align-items-center justify-content-center" style="width: 18px; height: 18px; padding: 0;">{{ idx + 1 }}</span>
                                    </div>
                                    <!-- Name & Tags -->
                                    <div style="flex: 1; min-width: 0;">
                                        <div class="d-flex align-items-center mb-1">
                                            <span class="font-weight-bold text-dark small text-truncate">
                                                {{ trader.trader_name }}
                                            </span>
                                            <b-badge v-if="getYouziLabel(trader.youzi_icon)" variant="secondary" class="ml-1 scale-8 font-weight-normal text-white bg-secondary border-0 flex-shrink-0">
                                                {{ getYouziLabel(trader.youzi_icon) }}
                                            </b-badge>
                                            <template v-if="trader.group_icon">
                                                <span v-for="(icon, idx) in parseGroupIcon(trader.group_icon)" :key="'gi-sell'+idx" 
                                                    class="badge badge-success text-white ml-1 scale-8 font-weight-normal flex-shrink-0">
                                                    {{ icon }}
                                                </span>
                                            </template>
                                        </div>
                                    </div>
                                    <!-- Amount -->
                                    <div class="text-right ml-2" style="min-width: 130px;">
                                        <div class="text-success font-weight-bold">
                                            {{ formatMoney(trader.sell_amount) }}
                                            <span class="small ml-1">({{ calcPct(trader.sell_amount) }})</span>
                                        </div>
                                        <div v-if="trader.buy_amount > 0" class="text-danger scale-8 small">
                                            买:{{ formatMoney(trader.buy_amount) }}
                                            <span class="ml-1">({{ calcPct(trader.buy_amount) }})</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="text-center p-5 text-muted">
            请选择股票查看详情
        </div>
        <stock-info-sidebar 
            v-if="stock" 
            :symbol_code="stock.stock_code" 
            :symbol_name="stock.stock_name" 
            :date_mark="date">
        </stock-info-sidebar>
    </div>
</template>

<script>
import StockKlineDay from '@/components/stock/StockKlineDay.vue'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar.vue'

export default {
    name: 'LhbDetailContent',
    components: { StockKlineDay, StockInfoSidebar },
    props: ['detailData', 'loading', 'stock', 'date', 'isMobile'],
    data() {
        return {
        }
    },
    computed: {
        traderGroups() {
            if (!this.detailData || !this.detailData.traders) return []
            
            // Group by reason_type
            const groups = {}
            this.detailData.traders.forEach(t => {
                const rt = t.reason_type || 'default'
                if (!groups[rt]) {
                    groups[rt] = { reason_type: rt, buy: [], sell: [] }
                }
                if (t.type === 1) { // Buy
                     groups[rt].buy.push(t)
                } else if (t.type === 2) { // Sell
                     groups[rt].sell.push(t)
                }
            })
            
            // Parse up_reason
            const reasonStr = this.detailData.detail.up_reason || ''
            const reasons = reasonStr.split(/[,，]/).filter(r => r && r.trim().length > 0)
            
            // Convert to array
            const result = Object.values(groups).map(g => {
                // Sort by rank
                g.buy.sort((a, b) => a.rank - b.rank)
                g.sell.sort((a, b) => a.rank - b.rank)
                
                // Calculate Top 5 Total
                g.buy_total = g.buy.reduce((sum, t) => sum + (t.buy_amount || 0), 0)
                g.sell_total = g.sell.reduce((sum, t) => sum + (t.sell_amount || 0), 0)
                
                // Assign label
                if (g.reason_type !== 'default') {
                    const idx = parseInt(g.reason_type)
                    if (!isNaN(idx) && idx < reasons.length) {
                         g.reason_label = reasons[idx]
                    } else {
                         g.reason_label = `上榜原因 ${g.reason_type}`
                    }
                } else {
                    g.reason_label = reasonStr
                }
                
                return g
            })
            
            // Sort groups: 0, 1, ...
            result.sort((a, b) => {
                 if (a.reason_type === 'default') return -1
                 if (b.reason_type === 'default') return 1
                 // Try numeric sort
                 const na = parseInt(a.reason_type)
                 const nb = parseInt(b.reason_type)
                 if (!isNaN(na) && !isNaN(nb)) return na - nb
                 return a.reason_type.localeCompare(b.reason_type)
            })
            
            return result
        }
    },
    methods: {
        openStockSidebar() {
            this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
        },
        parseGroupIcon(iconStr) {
            if (!iconStr) return []
            // group_icon is comma-separated string like "1,2,3"
            return iconStr.split(',').filter(s => s.trim())
        },
        getYouziLabel(val) {
            if (val == 1) return '一线游资'
            if (val == 2) return '顶级游资'
            if (val == 3) return '知名游资'
            return null
        },
        getColorClass(val) {
            if (val > 0) return 'text-danger'
            if (val < 0) return 'text-success'
            return ''
        },
        formatMoney(val) {
            if (!val) return '0'
            let absVal = Math.abs(val)
            let res = ''
            if (absVal >= 100000000) {
                res = (absVal / 100000000).toFixed(2) + '亿'
            } else if (absVal >= 10000) {
                res = (absVal / 10000000).toFixed(2) + '千万'
            } else {
                res = absVal.toFixed(2)
            }
            if (val < 0) return '-' + res
            return res
        },
        calcPct(amount) {
            const turnover = (this.detailData && this.detailData.detail && this.detailData.detail.turnover) ? this.detailData.detail.turnover : 0
            if (!turnover || turnover <= 0) return '--'
            const pct = (Math.abs(amount) * 100) / turnover
            return pct.toFixed(2) + '%'
        }
    }
}
</script>

<style scoped>
.text-danger { color: #e33e33 !important; }
.text-success { color: #17803d !important; }
.badge-danger { background-color: #e33e33; }
.badge-success { background-color: #17803d; }
.scale-8 { transform: scale(0.85); transform-origin: left center; display: inline-block; }

@media (max-width: 768px) {
    .kline-container {
        min-height: 250px !important;
    }
}
</style>
